import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useOrders } from '@/services/order-service';
import { OrderResponse, OrderStatus } from '@/types/order-type';
import { format } from 'date-fns';
import { ArrowRight, Calendar, FileText, History, Search } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'wouter';

// Use centralized types from order-type.ts

// Format currency
const formatCurrency = (amount: string | number) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 2,
  }).format(num);
};

export default function SearchOrderPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState<OrderResponse[]>([]);

  // Use real service call instead of mock data
  const { items: ordersData, isLoading, error } = useOrders();
  const orders = (ordersData as OrderResponse[]) || [];

  // Handle search
  const handleSearch = () => {
    setIsSearching(true);

    let filtered = [...orders];

    // Filter by order ID, notes, or tracking number
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.id?.toLowerCase().includes(term) ||
          order.notes?.toLowerCase().includes(term) ||
          order.trackingNumber?.toLowerCase().includes(term),
      );
    }

    // Filter by date
    if (selectedDate) {
      const dateStr = selectedDate.split('T')[0];
      filtered = filtered.filter((order) => order.createdAt && order.createdAt.split('T')[0] === dateStr);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((order) => order.status === selectedStatus);
    }

    // Filter by tracking number
    if (trackingNumber) {
      // Assuming there's a trackingNumber field on orders
      filtered = filtered.filter((order) =>
        order.trackingNumber?.toLowerCase().includes(trackingNumber.toLowerCase()),
      );
    }

    setFilteredOrders(filtered);
  };

  // Clear search filters
  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedStatus('');
    setTrackingNumber('');
    setIsSearching(false);
    setFilteredOrders([]);
  };

  // Get status badge color
  const getStatusBadgeColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.DRAFT:
        return 'bg-amber-500';
      case OrderStatus.CONFIRMED:
        return 'bg-blue-500';
      case OrderStatus.RETURNED:
        return 'bg-purple-500';
      case OrderStatus.CANCELLED:
        return 'bg-red-500';
      case OrderStatus.REFUNDED:
        return 'bg-orange-500';
      case OrderStatus.PAID:
        return 'bg-emerald-600';
      default:
        return 'bg-amber-500';
    }
  };

  // Get display data
  const displayOrders = isSearching ? filteredOrders : orders;

  return (
    <div className="container mx-auto py-6 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-700">Search Orders</h1>
        <Button variant="outline" className="gap-2" onClick={() => setLocation('/orders')}>
          <History size={18} />
          <span>Orders List</span>
        </Button>
      </div>

      {/* Search Form */}
      <Card className="mb-6 border-blue-100">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">
                Customer Name / Order ID
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Order Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  className="pl-10"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">Order Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-gray-700">
                Tracking Number
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Enter tracking #"
                  className="pl-10"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleClearSearch}>
              Clear Filters
            </Button>
            <Button
              className="gap-2 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
              onClick={handleSearch}
            >
              <Search size={18} />
              <span>Search Orders</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <>
          {isSearching && (
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-700">
                Search Results
                <span className="ml-2 text-gray-500">({filteredOrders.length} orders found)</span>
              </h2>
              {filteredOrders.length === 0 && (
                <Button variant="link" onClick={handleClearSearch} className="text-blue-600">
                  Clear search and show all orders
                </Button>
              )}
            </div>
          )}

          {displayOrders.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-100">
              <div className="mb-4">
                <FileText className="h-12 w-12 text-gray-400 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">No Orders Found</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                {isSearching
                  ? 'No orders match your search criteria. Try adjusting your filters.'
                  : 'There are no orders in the system yet.'}
              </p>
              {isSearching && (
                <Button variant="outline" onClick={handleClearSearch} className="mx-auto">
                  Clear Search Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader className="bg-blue-50">
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-blue-50 cursor-pointer">
                      <TableCell
                        className="font-medium"
                        onClick={() => setLocation(`/orders/${order.id}`)}
                      >
                        #{order.id.substring(0, 8)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{order.createdAt && format(new Date(order.createdAt), 'MMM dd, yyyy')}</span>
                          <span className="text-xs text-gray-500">
                            {order.createdAt && format(new Date(order.createdAt), 'h:mm a')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>Customer ID: {order.customerId}</span>
                          <span className="text-xs text-gray-500">
                            Use customer detail page for full info
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={getStatusBadgeColor(order.status)}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                          {order.paymentStatus && (
                            <Badge
                              variant="outline"
                              className={
                                order.paymentStatus === 'Paid'
                                  ? 'text-emerald-600 border-emerald-200'
                                  : 'text-amber-600 border-amber-200'
                              }
                            >
                              {order.paymentStatus}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(order.totalAmount || '0')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 flex items-center gap-1"
                          onClick={() => setLocation(`/orders/${order.id}`)}
                        >
                          <span>View</span>
                          <ArrowRight size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
